import { Typography } from '@remarkable/ark-web';
import { useAtom } from 'jotai';
import type { ReactNode } from 'react';

import type { ConnectInterval } from 'src/api/endpoints/cloudConnect.types';
import type { Subscription } from 'src/api/endpoints/storeApi.types';
import { usePricePreview } from 'src/api/queries';
import { useOfferExpirationDetails } from 'src/apps/manageSubscription/app/utils/getOfferExpirationDetails';
import { useHasClickedBiennialOffer } from 'src/apps/manageSubscription/app/utils/hasBiennialOfferStore';
import { atomWithLocalStorage } from 'src/utils/atomWithLocalStorage';
import { formatAmount } from 'src/utils/productUtils';
import { URLS } from 'src/utils/urls/urls';

import { Trans, useLingui } from '@lingui/react/macro';
import {
  CAMPAIGN_PRICE_PREVIEW_STALE_TIME,
  canShowChangePaymentIntervalBanner,
  getBiennialSavings,
  getPaymentIntervalOption,
  hasPaymentInterval,
} from './ChangePaymentIntervalBanners.utils';
import { Link2 } from './Link2';
import { NotificationBanner } from './NotificationBanner';

const annualNotificationDismissedAtom = atomWithLocalStorage<boolean>(
  'annualNotificationDismissed',
  false
);

const biennialNotificationDismissedAtom = atomWithLocalStorage<boolean>(
  'biennialNotificationDismissed',
  false
);

type PaymentIntervalBannerProps = {
  subscription?: Subscription;
  componentLocation: string;
  className?: string;
};

function PaymentIntervalNotificationBanner({
  children,
  className,
  dataCy,
  onDismiss,
  title,
}: {
  children: ReactNode;
  className?: string;
  dataCy: string;
  onDismiss: () => void;
  title: ReactNode;
}) {
  return (
    <div className={className} data-cy={dataCy}>
      <NotificationBanner onClick={onDismiss}>
        <div>
          <Typography variant="heading-subtle-lg" as="h3">
            {title}
          </Typography>

          <Typography variant="body-md">{children}</Typography>
        </div>
      </NotificationBanner>
    </div>
  );
}

function PaymentIntervalLink({
  children,
  componentLocation,
  dataCy,
  updateBillingInterval,
}: {
  children: ReactNode;
  componentLocation: string;
  dataCy: string;
  updateBillingInterval: ConnectInterval;
}) {
  return (
    <Link2
      inline
      to={URLS.SUBSCRIPTION_PAYMENT}
      search={{ updateBillingInterval }}
      data-cy={dataCy}
      componentLocation={componentLocation}
      className="font-bold"
    >
      {children}
    </Link2>
  );
}

const AnnualBillingBanner = ({
  subscription,
  componentLocation,
  className,
}: PaymentIntervalBannerProps) => {
  const { i18n } = useLingui();
  const [notificationDismissed, setNotificationDismissed] = useAtom(
    annualNotificationDismissedAtom
  );

  const annualOfferIsActive = !useOfferExpirationDetails().offerHasEnded;
  const canShowBanner =
    canShowChangePaymentIntervalBanner(subscription) &&
    hasPaymentInterval(subscription, 'monthly') &&
    !notificationDismissed;
  const annualOption = getPaymentIntervalOption(subscription, 'annually');
  const annualCampaignPricePreview = usePricePreview('annual', {
    enabled: canShowBanner && annualOfferIsActive,
    staleTime: CAMPAIGN_PRICE_PREVIEW_STALE_TIME,
  });

  if (!canShowBanner) {
    return null;
  }

  if (annualOfferIsActive) {
    const annualCampaignPrice = annualCampaignPricePreview.data;
    if (!annualCampaignPrice) {
      return null;
    }

    const annualPriceText = formatAmount(
      annualCampaignPrice.totalAmount,
      annualCampaignPrice.currency,
      i18n.locale
    );

    return (
      <PaymentIntervalNotificationBanner
        className={className}
        dataCy="change-to-annual-banner"
        onDismiss={() => setNotificationDismissed(true)}
        title={<Trans>Annual billing now available</Trans>}
      >
        <Trans>
          Pay {annualPriceText}/year and get three months free when you{' '}
          <PaymentIntervalLink
            componentLocation={componentLocation}
            dataCy="change-to-annual-banner-link"
            updateBillingInterval="annual"
          >
            switch to annual billing
          </PaymentIntervalLink>
          .
        </Trans>
      </PaymentIntervalNotificationBanner>
    );
  }

  if (!annualOption) {
    return null;
  }

  const annualPriceText = formatAmount(
    annualOption.amount.total,
    annualOption.amount.currency,
    i18n.locale
  );

  return (
    <PaymentIntervalNotificationBanner
      className={className}
      dataCy="change-to-annual-banner"
      onDismiss={() => setNotificationDismissed(true)}
      title={<Trans>Annual billing now available</Trans>}
    >
      <Trans>
        Pay {annualPriceText}/year and get two months free when you{' '}
        <PaymentIntervalLink
          componentLocation={componentLocation}
          dataCy="change-to-annual-banner-link"
          updateBillingInterval="annual"
        >
          switch to annual billing
        </PaymentIntervalLink>
        .
      </Trans>
    </PaymentIntervalNotificationBanner>
  );
};

const BiennialBillingBanner = ({
  subscription,
  componentLocation,
  className,
}: PaymentIntervalBannerProps) => {
  const { i18n } = useLingui();
  const [hasClickedBiennialOffer] = useHasClickedBiennialOffer();
  const [notificationDismissed, setNotificationDismissed] = useAtom(
    biennialNotificationDismissedAtom
  );

  const biennialOfferDetails = useOfferExpirationDetails();
  const savings = getBiennialSavings(subscription);
  const daysUntilOfferEnds = biennialOfferDetails.daysUntilOfferEnds;

  if (
    !canShowChangePaymentIntervalBanner(subscription) ||
    !hasPaymentInterval(subscription, 'annually') ||
    !hasClickedBiennialOffer ||
    notificationDismissed ||
    biennialOfferDetails.offerHasEnded ||
    !savings
  ) {
    return null;
  }

  const savingsText = formatAmount(
    savings.total,
    savings.currency,
    i18n.locale
  );

  return (
    <PaymentIntervalNotificationBanner
      className={className}
      dataCy="change-to-biennial-banner"
      onDismiss={() => setNotificationDismissed(true)}
      title={<Trans>Get 6 months free, with 2 years of Connect</Trans>}
    >
      <Trans>
        For the next {daysUntilOfferEnds} days, you can get 2 years of Connect
        and save {savingsText}. That&apos;s 6 months free, when you{' '}
        <PaymentIntervalLink
          componentLocation={componentLocation}
          dataCy="change-to-biennial-banner-link"
          updateBillingInterval="biennial"
        >
          switch to 2 year plan
        </PaymentIntervalLink>
        .
      </Trans>
    </PaymentIntervalNotificationBanner>
  );
};

export function ChangePaymentIntervalBanners(
  props: PaymentIntervalBannerProps
) {
  return (
    <>
      <AnnualBillingBanner {...props} />
      <BiennialBillingBanner {...props} />
    </>
  );
}
